import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Plus, X, ExternalLink, Play, Pause, PowerOff, Download } from "lucide-react";

const PLACEMENTS = ["free_result", "paid_plan", "salon_result", "pdf_footer", "marketing_email"];
const AUDIENCES = ["female", "male", "salon", "individual"];
const STATUSES = ["draft", "active", "paused", "inactive"];

const EMPTY = {
  brand_name: "",
  logo_url: "",
  desktop_banner_url: "",
  mobile_banner_url: "",
  email_banner_url: "",
  destination_url: "",
  tracking_params: "",
  starts_at: "",
  ends_at: "",
  status: "draft",
  countries: [],
  languages: ["en"],
  audiences: ["individual"],
  profiles: [],
  placements: ["free_result"],
  copy_en: "",
  copy_it: "",
  impression_limit: 0,
  priority: 0,
  fee_amount: 0,
  fee_currency: "EUR",
  invoice_status: "",
  payment_status: "",
  notes: "",
};

const StatusPill = ({ status }) => {
  const map = {
    active: "bg-[#EAF3EC] text-[#4A6D50] border-[#CDE0D3]",
    paused: "bg-[#FFF6E4] text-[#8A6A2E] border-[#F4E1B7]",
    draft: "bg-[#F8F6F2] text-[#6F6B66] border-[#ECE7DF]",
    inactive: "bg-[#FCECEA] text-[#8A3B2E] border-[#F0C7C0]",
  };
  return (
    <span className={`inline-block rounded-full border px-2.5 py-0.5 text-[10px] kt-tracking uppercase ${map[status] || map.draft}`}>
      {status || "draft"}
    </span>
  );
};

export default function AdminCampaigns() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ total: 0, active: 0 });
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState(null);

  const load = async () => {
    const r = await axios.get(`${API}/admin/campaigns`);
    setItems(r.data.items);
    setMeta({ total: r.data.total, active: r.data.active });
  };
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ ...EMPTY });
  const openEdit = (c) => setEditing({ ...c });

  const saveOne = async () => {
    setBusy(true);
    try {
      const payload = {
        ...editing,
        impression_limit: Number(editing.impression_limit) || 0,
        priority: Number(editing.priority) || 0,
        fee_amount: Number(editing.fee_amount) || 0,
      };
      if (editing.campaign_id) {
        await axios.patch(`${API}/admin/campaigns/${editing.campaign_id}`, payload);
      } else {
        await axios.post(`${API}/admin/campaigns`, payload);
      }
      toast.success("Campaign saved");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const setStatus = async (c, status) => {
    await axios.patch(`${API}/admin/campaigns/${c.campaign_id}`, { status });
    toast.success(`Campaign ${status}`);
    await load();
  };

  const remove = async (c) => {
    if (!window.confirm(`Deactivate campaign "${c.brand_name}"?`)) return;
    await axios.delete(`${API}/admin/campaigns/${c.campaign_id}`);
    toast.success("Campaign deactivated");
    await load();
  };

  const openStats = async (c) => {
    const r = await axios.get(`${API}/admin/campaigns/${c.campaign_id}/stats`);
    setStats({ campaign: c, ...r.data });
  };

  const csvUrl = (c) => `${API}/admin/campaigns/${c.campaign_id}/export.csv`;

  const toggle = (key, val) => {
    const cur = editing[key] || [];
    setEditing({ ...editing, [key]: cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val] });
  };

  return (
    <div className="space-y-8" data-testid="admin-campaigns">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-serif text-3xl text-[#383633] tracking-tight">Brand Partnerships & Advertising</h1>
          <p className="text-[#6F6B66] mt-1 text-sm">
            Manage paid partner banners across the free result, paid plan, salon result, PDF and marketing emails.
            KT Milano permanent branding always stays on top — partner ads never replace it.
          </p>
        </div>
        <Button onClick={openNew} data-testid="new-campaign"
          className="rounded-full bg-[#383633] hover:bg-[#4A4238] text-white h-11 px-5 text-xs kt-tracking uppercase">
          <Plus className="h-3.5 w-3.5" /> New campaign
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <OverviewCard title="Total campaigns" value={meta.total} />
        <OverviewCard title="Active" value={meta.active} />
      </div>

      <div className="rounded-2xl bg-white border kt-line p-6 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] kt-tracking uppercase text-[#6F6B66] border-b kt-line">
              <th className="text-left py-2 font-normal">Brand</th>
              <th className="text-left py-2 font-normal">Status</th>
              <th className="text-left py-2 font-normal">Window</th>
              <th className="text-left py-2 font-normal">Placements</th>
              <th className="text-left py-2 font-normal">Impr / Clicks</th>
              <th className="text-right py-2 font-normal">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.campaign_id} className="border-b kt-line" data-testid={`campaign-row-${c.campaign_id}`}>
                <td className="py-3">
                  <button onClick={() => openEdit(c)} className="text-[#383633] hover:underline text-left">
                    {c.brand_name}
                  </button>
                  <div className="text-[11px] text-[#6F6B66]">{c.audiences?.join(", ")}</div>
                </td>
                <td className="py-3"><StatusPill status={c.status} /></td>
                <td className="py-3 text-[11px] text-[#6F6B66]">
                  {c.starts_at ? new Date(c.starts_at).toLocaleDateString() : "—"}
                  {" → "}
                  {c.ends_at ? new Date(c.ends_at).toLocaleDateString() : "—"}
                </td>
                <td className="py-3 text-[11px] text-[#6F6B66]">{(c.placements || []).join(", ")}</td>
                <td className="py-3 text-[#383633]">
                  {c.impressions || 0} / {c.clicks || 0}
                  {c.impression_limit ? <span className="text-[11px] text-[#6F6B66]"> · cap {c.impression_limit}</span> : null}
                </td>
                <td className="py-3 text-right">
                  <div className="inline-flex items-center gap-3">
                    <button onClick={() => openStats(c)} data-testid={`stats-${c.campaign_id}`}
                      className="text-xs kt-tracking uppercase text-[#6F6B66] hover:text-[#383633]">Stats</button>
                    {c.status !== "active" ? (
                      <button onClick={() => setStatus(c, "active")} data-testid={`activate-${c.campaign_id}`}
                        className="inline-flex items-center gap-1 text-xs text-[#4A6D50] hover:underline">
                        <Play className="h-3 w-3" /> Activate
                      </button>
                    ) : (
                      <button onClick={() => setStatus(c, "paused")} data-testid={`pause-${c.campaign_id}`}
                        className="inline-flex items-center gap-1 text-xs text-[#8A6A2E] hover:underline">
                        <Pause className="h-3 w-3" /> Pause
                      </button>
                    )}
                    <a href={csvUrl(c)} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-[#6F6B66] hover:text-[#383633]">
                      <Download className="h-3 w-3" /> CSV
                    </a>
                    <button onClick={() => remove(c)} data-testid={`delete-${c.campaign_id}`}
                      className="inline-flex items-center gap-1 text-xs text-[#8A3B2E] hover:underline">
                      <PowerOff className="h-3 w-3" /> End
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={6} className="py-8 text-center text-[#6F6B66]">No campaigns yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-auto"
             onClick={() => setEditing(null)}>
          <div className="bg-white max-w-3xl w-full my-8 rounded-2xl border kt-line p-6"
               onClick={(e) => e.stopPropagation()} data-testid="campaign-editor">
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66]">
                  {editing.campaign_id ? "Edit campaign" : "New campaign"}
                </div>
                <h2 className="font-serif text-2xl text-[#383633]">{editing.brand_name || "Untitled partner"}</h2>
              </div>
              <button onClick={() => setEditing(null)} className="text-[#6F6B66] hover:text-[#383633]">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <F label="Brand name *"><Input data-testid="camp-brand" value={editing.brand_name}
                onChange={(e) => setEditing({ ...editing, brand_name: e.target.value })} /></F>
              <F label="Destination URL"><Input value={editing.destination_url}
                onChange={(e) => setEditing({ ...editing, destination_url: e.target.value })} /></F>
              <F label="Logo URL"><Input value={editing.logo_url}
                onChange={(e) => setEditing({ ...editing, logo_url: e.target.value })} /></F>
              <F label="Tracking params (utm_source=...)"><Input value={editing.tracking_params}
                onChange={(e) => setEditing({ ...editing, tracking_params: e.target.value })} /></F>
              <F label="Desktop banner URL (~728×90)"><Input value={editing.desktop_banner_url}
                onChange={(e) => setEditing({ ...editing, desktop_banner_url: e.target.value })} /></F>
              <F label="Mobile banner URL (~320×100)"><Input value={editing.mobile_banner_url}
                onChange={(e) => setEditing({ ...editing, mobile_banner_url: e.target.value })} /></F>
              <F label="Email banner URL (max 600w)"><Input value={editing.email_banner_url}
                onChange={(e) => setEditing({ ...editing, email_banner_url: e.target.value })} /></F>
              <F label="Impression limit (0 = unlimited)"><Input type="number" value={editing.impression_limit}
                onChange={(e) => setEditing({ ...editing, impression_limit: e.target.value })} /></F>
              <F label="Starts at (ISO)"><Input value={editing.starts_at}
                placeholder="2026-03-01T00:00:00+00:00"
                onChange={(e) => setEditing({ ...editing, starts_at: e.target.value })} /></F>
              <F label="Ends at (ISO)"><Input value={editing.ends_at}
                placeholder="2026-06-30T23:59:59+00:00"
                onChange={(e) => setEditing({ ...editing, ends_at: e.target.value })} /></F>
              <F label="Copy (EN)"><Input value={editing.copy_en}
                onChange={(e) => setEditing({ ...editing, copy_en: e.target.value })} /></F>
              <F label="Copy (IT)"><Input value={editing.copy_it}
                onChange={(e) => setEditing({ ...editing, copy_it: e.target.value })} /></F>
              <F label="Countries (ISO, comma)">
                <Input value={(editing.countries || []).join(",")}
                  onChange={(e) => setEditing({ ...editing, countries: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
              </F>
              <F label="Languages (comma)">
                <Input value={(editing.languages || []).join(",")}
                  onChange={(e) => setEditing({ ...editing, languages: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
              </F>
              <F label="Profiles (comma — cosmetic result names)">
                <Input value={(editing.profiles || []).join(",")}
                  onChange={(e) => setEditing({ ...editing, profiles: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
              </F>
              <F label="Priority (higher = shown first)"><Input type="number" value={editing.priority}
                onChange={(e) => setEditing({ ...editing, priority: e.target.value })} /></F>
              <F label="Fee (EUR)"><Input type="number" step="0.01" value={editing.fee_amount}
                onChange={(e) => setEditing({ ...editing, fee_amount: e.target.value })} /></F>
              <F label="Invoice status"><Input value={editing.invoice_status}
                placeholder="unbilled / sent / paid / overdue"
                onChange={(e) => setEditing({ ...editing, invoice_status: e.target.value })} /></F>
              <F label="Payment status"><Input value={editing.payment_status}
                onChange={(e) => setEditing({ ...editing, payment_status: e.target.value })} /></F>
              <div className="sm:col-span-2">
                <Label className="text-xs kt-tracking uppercase text-[#6F6B66]">Audiences</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {AUDIENCES.map((a) => (
                    <button key={a} type="button"
                      data-testid={`camp-aud-${a}`}
                      onClick={() => toggle("audiences", a)}
                      className={`rounded-full px-3 py-1.5 text-xs kt-tracking uppercase border ${(editing.audiences || []).includes(a) ? "bg-[#383633] text-white border-[#383633]" : "border-[#ECE7DF] text-[#6F6B66] hover:text-[#383633]"}`}>
                      {a}
                    </button>
                  ))}
                </div>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs kt-tracking uppercase text-[#6F6B66]">Placements</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {PLACEMENTS.map((p) => (
                    <button key={p} type="button"
                      data-testid={`camp-place-${p}`}
                      onClick={() => toggle("placements", p)}
                      className={`rounded-full px-3 py-1.5 text-xs kt-tracking uppercase border ${(editing.placements || []).includes(p) ? "bg-[#383633] text-white border-[#383633]" : "border-[#ECE7DF] text-[#6F6B66] hover:text-[#383633]"}`}>
                      {p}
                    </button>
                  ))}
                </div>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs kt-tracking uppercase text-[#6F6B66]">Status</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {STATUSES.map((s) => (
                    <button key={s} type="button"
                      onClick={() => setEditing({ ...editing, status: s })}
                      className={`rounded-full px-3 py-1.5 text-xs kt-tracking uppercase border ${editing.status === s ? "bg-[#383633] text-white border-[#383633]" : "border-[#ECE7DF] text-[#6F6B66] hover:text-[#383633]"}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <F label="Notes" className="sm:col-span-2">
                <Input value={editing.notes || ""}
                  onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
              </F>
            </div>

            {/* Preview */}
            {editing.desktop_banner_url && (
              <div className="mt-6">
                <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-2">Desktop preview</div>
                <img src={editing.desktop_banner_url} alt={editing.brand_name}
                  loading="lazy" decoding="async"
                  className="w-full max-w-2xl border kt-line rounded-xl" />
              </div>
            )}

            <div className="mt-6 flex items-center justify-end gap-3">
              <button onClick={() => setEditing(null)}
                className="text-xs kt-tracking uppercase text-[#6F6B66] hover:text-[#383633]">Cancel</button>
              <Button onClick={saveOne} disabled={busy} data-testid="save-campaign"
                className="rounded-full bg-[#383633] hover:bg-[#4A4238] text-white h-11 px-6 text-xs kt-tracking uppercase">
                Save campaign
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Stats modal */}
      {stats && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
             onClick={() => setStats(null)}>
          <div className="bg-white max-w-lg w-full rounded-2xl border kt-line p-6"
               onClick={(e) => e.stopPropagation()} data-testid="campaign-stats">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66]">Campaign stats</div>
                <h2 className="font-serif text-2xl text-[#383633]">{stats.campaign.brand_name}</h2>
              </div>
              <button onClick={() => setStats(null)} className="text-[#6F6B66] hover:text-[#383633]">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-3">
              <OverviewCard title="Impressions" value={stats.impressions} />
              <OverviewCard title="Clicks" value={stats.clicks} />
              <OverviewCard title="CTR" value={`${stats.ctr}%`} />
            </div>
            <div className="mt-4">
              <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-2">Impressions by placement</div>
              <ul className="text-sm space-y-1">
                {Object.entries(stats.impressions_by_placement || {}).map(([k, v]) => (
                  <li key={k} className="flex items-center justify-between border-b kt-line py-1">
                    <span className="text-[#383633]">{k}</span>
                    <span className="font-mono text-[#6F6B66]">{v}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function F({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs kt-tracking uppercase text-[#6F6B66]">{label}</Label>
      <div className="mt-2 [&_input]:border-[#ECE7DF] [&_input]:rounded-xl [&_input]:h-11">{children}</div>
    </div>
  );
}
function OverviewCard({ title, value }) {
  return (
    <div className="rounded-2xl bg-white border kt-line p-4">
      <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66]">{title}</div>
      <div className="font-serif text-2xl text-[#383633] mt-1">{value}</div>
    </div>
  );
}
